import { motion } from "framer-motion";

// Le Cœur de Tension Dialectique — pièce maîtresse animée.
// Ambiante (dans les modales concept) OU réactive en direct dans l'Atelier :
//  - variant "tug"  + lean (-1..1)      → penche vers l'Ancre (bleu) ou le Feu (ambre)
//  - variant "scale"+ tilt (-1..1)      → la balance penche selon le verdict
//  - variant "ember"+ intensity, pulseKey → la braise s'embrase à chaque assaut
const clamp = (v) => Math.max(-1, Math.min(1, v));

export function DialecticCore({ testId = "dialectic-core", label, variant = "tug", lean, tilt, intensity, pulseKey }) {
  return (
    <div className="relative mt-4 mb-1 flex flex-col items-center" data-testid={testId} aria-hidden>
      <div className="relative w-full h-14 flex items-center justify-center">
        {variant === "tug" && <TugCore lean={lean} />}
        {variant === "scale" && <ScaleCore tilt={tilt} />}
        {variant === "ember" && <EmberCore intensity={intensity} pulseKey={pulseKey} />}
      </div>
      {label && (
        <span className="mt-0.5 text-[0.6rem] uppercase tracking-[0.3em] text-foreground/40">{label}</span>
      )}
    </div>
  );
}

function TugCore({ lean }) {
  const live = typeof lean === "number";
  return (
    <>
      <div className="absolute inset-x-8 h-[2px] rounded-full bg-gradient-to-r from-sky-300/50 via-white/15 to-primary/60" />
      {!live ? (
        <>
          <motion.div className="absolute w-1.5 h-1.5 rounded-full bg-sky-300 shadow-[0_0_10px_rgba(125,211,252,0.9)]" style={{ left: "12%" }}
            animate={{ x: [0, 180, 0], opacity: [0, 1, 0] }} transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }} />
          <motion.div className="absolute w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_10px_rgba(245,158,11,0.9)]" style={{ right: "12%" }}
            animate={{ x: [0, -180, 0], opacity: [0, 1, 0] }} transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut", delay: 1.7 }} />
        </>
      ) : (
        <>
          <div className="absolute left-8 w-2 h-2 rounded-full bg-sky-300/70" />
          <div className="absolute right-8 w-2 h-2 rounded-full bg-primary/70" />
        </>
      )}
      <motion.div
        className="relative w-11 h-11"
        animate={live ? { x: clamp(lean) * 22, scale: 1.06 } : { x: [-16, 16, -16], scale: [1, 1.14, 1] }}
        transition={live ? { type: "spring", stiffness: 110, damping: 13 } : { duration: 4.2, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-sky-300 via-amber-100 to-primary blur-md opacity-70" />
        <div className="absolute inset-[5px] rounded-full bg-gradient-to-r from-sky-300/80 to-primary/80 border border-white/30" />
        <motion.div className="absolute inset-0 rounded-full border border-white/40"
          animate={{ scale: [1, 1.7], opacity: [0.55, 0] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }} />
      </motion.div>
    </>
  );
}

function ScaleCore({ tilt }) {
  const live = typeof tilt === "number";
  const rot = live ? clamp(tilt) * 14 : undefined;
  return (
    <div className="relative w-52 h-14 flex items-end justify-center">
      <div className="absolute bottom-1 w-16 h-[2px] bg-primary/40 rounded-full" />
      <div className="absolute bottom-[6px] w-0 h-0 border-l-[8px] border-r-[8px] border-b-[15px] border-l-transparent border-r-transparent border-b-primary/70" />
      <motion.div
        className="absolute bottom-[20px] w-40 h-[2px] rounded-full bg-gradient-to-r from-red-300/70 via-primary to-emerald-300/70 origin-center"
        animate={live ? { rotate: rot } : { rotate: [0, 7, -7, 3, 0] }}
        transition={live ? { type: "spring", stiffness: 55, damping: 11 } : { duration: 4.4, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="absolute -left-1 -bottom-1.5 w-2.5 h-2.5 rounded-full bg-red-300 shadow-[0_0_8px_rgba(252,165,165,0.8)]" />
        <div className="absolute -right-1 -bottom-1.5 w-2.5 h-2.5 rounded-full bg-emerald-300 shadow-[0_0_8px_rgba(110,231,183,0.8)]" />
      </motion.div>
      <motion.div className="absolute bottom-[16px] w-3.5 h-3.5 rounded-full bg-primary shadow-[0_0_14px_rgba(245,158,11,0.9)] border border-amber-100/50"
        animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} />
    </div>
  );
}

function EmberCore({ intensity, pulseKey }) {
  const live = typeof intensity === "number";
  const i = live ? Math.min(intensity / 6, 1) : 0;
  const baseScale = 1 + i * 0.35;
  const glow = 0.4 + i * 0.5;
  return (
    <motion.div
      className="relative"
      style={{ width: 56, height: 56 }}
      animate={{ scale: live ? [baseScale, baseScale * 1.06, baseScale] : [1, 1.14, 1] }}
      transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
    >
      <motion.div className="absolute inset-0 rounded-full bg-primary blur-xl"
        animate={{ opacity: live ? [glow * 0.8, glow, glow * 0.8] : [0.35, 0.85, 0.35] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }} />
      <div className="absolute inset-[6px] rounded-full bg-gradient-to-t from-amber-700 via-primary to-amber-200 border border-amber-200/40" />
      <motion.div className="absolute inset-0 rounded-full border border-primary/50"
        animate={{ scale: [1, 1.85], opacity: [0.6, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }} />
      {live && (
        <motion.div key={pulseKey} className="absolute inset-0 rounded-full border-2 border-amber-200"
          initial={{ scale: 1, opacity: 0.9 }} animate={{ scale: 2.6, opacity: 0 }} transition={{ duration: 0.9, ease: "easeOut" }} />
      )}
    </motion.div>
  );
}
